import { useState } from "react";
import "react-phone-input-2/lib/bootstrap.css";
import PhoneNumberInput from "./PhoneNumberInput";
import { toast } from "react-toastify";
import { MdPerson, MdBadge, MdArrowBack, MdChevronRight } from "react-icons/md";
import CountrySelect from "../commonUtils/CountrySelect";
import { getAlpha2, getCountryName } from "../commonUtils/countries";

const toCountryCode = (value) => {
  const code = getAlpha2(value || "");
  return code ? code.toUpperCase() : "";
};

const isEmpty = (v) => !v || !String(v).trim();

const PersonForm = ({
  title,
  initialData = {},
  lockOnCountryName = null,
  onSubmit,
  onBack,
  step,
  showPanel = true,
  showHeader = true,
  showStepBadge = true,
  formClassName = "p-md-4",
  actionsRenderer,
  primaryButtonLabel = "Next",
  primaryButtonIcon = <MdChevronRight />,
}) => {
  const normalizedLock = toCountryCode(lockOnCountryName);
  const isCountryLocked = !!normalizedLock;

  const [person, setPerson] = useState({
    name: initialData.name || "",
    lastName: initialData.lastName || "",
    phone: initialData.phone || "",
    country: toCountryCode(lockOnCountryName) || toCountryCode(initialData.country) || "",
    dialCode: initialData.dialCode || "",
    nationalNumber: initialData.nationalNumber || "",
    phoneValid: initialData.phoneValid ?? false,
  });

  const [touched, setTouched] = useState({ name: false, lastName: false, phone: false });
  const [showErrors, setShowErrors] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPerson((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
  };

  const selectedCountry = isCountryLocked ? normalizedLock : person.country;
  const phoneInputKey = selectedCountry || "default";

  const nameInvalid = isEmpty(person.name);
  const lastInvalid = isEmpty(person.lastName);
  const phoneInvalid = isEmpty(person.phone) || !person.phoneValid;

  const shouldShowNameErr = (showErrors || touched.name) && nameInvalid;
  const shouldShowLastErr = (showErrors || touched.lastName) && lastInvalid;
  const shouldShowPhoneErr = (showErrors || touched.phone) && phoneInvalid;

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowErrors(true);
    const payload = {
      ...person,
      country: isCountryLocked ? normalizedLock : person.country,
      countryName: isCountryLocked
        ? getCountryName(normalizedLock) || normalizedLock
        : getCountryName(person.country) || person.country,
    };

    if (isEmpty(payload.name) || isEmpty(payload.lastName) || isEmpty(payload.phone) || isEmpty(payload.country)) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (!payload.phoneValid) return;

    onSubmit(payload);
  };

  const actionContent = actionsRenderer ? (
    actionsRenderer({ step, onBack })
  ) : (
    <div className="ui-actions mt-4 w-100 justify-content-between">
      {step !== 1 ? (
        <button
          type="button"
          className="ui-btn"
          onClick={onBack}
        >
          <MdArrowBack /> Prev
        </button>
      ) : <span />}
      <button
        type="submit"
        className="btn btn-primary rounded-pill d-inline-flex align-items-center gap-2 ms-auto"
      >
        {primaryButtonLabel}
        {primaryButtonIcon ? primaryButtonIcon : null}
      </button>
    </div>
  );

  const content = (
    <>
      {showHeader && (
        <header className="ui-panel__header">
          <div className="d-flex align-items-center justify-content-between">
            <h3 className="ui-title mb-0">{title}</h3>
            {showStepBadge && (
              <span className="badge bg-body border text-body d-flex align-items-center gap-1">
                <MdBadge /> Step {step ?? 1}
              </span>
            )}
          </div>
        </header>
      )}

      <form onSubmit={handleSubmit} className={formClassName}>
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <label className="form-label">First Name, Middle Name</label>
            <div className="input-group">
              <span className="input-group-text bg-body"><MdPerson /></span>
              <input
                name="name"
                type="text"
                className={`form-control ${shouldShowNameErr ? "is-invalid" : ""}`}
                value={person.name}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                autoComplete="given-name"
                aria-invalid={shouldShowNameErr || undefined}
              />
              {shouldShowNameErr && <div className="invalid-feedback">First (and middle) name is required.</div>}
            </div>
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label">Last Name</label>
            <div className="input-group">
              <span className="input-group-text bg-body"><MdPerson /></span>
              <input
                name="lastName"
                type="text"
                className={`form-control ${shouldShowLastErr ? "is-invalid" : ""}`}
                value={person.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                autoComplete="family-name"
                aria-invalid={shouldShowLastErr || undefined}
              />
              {shouldShowLastErr && <div className="invalid-feedback">Last name is required.</div>}
            </div>
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label">Country</label>
            <CountrySelect
              value={selectedCountry}
              locked={!!isCountryLocked}
              showFlagInValue
              onChange={(code) => {
                if (isCountryLocked) return;
                const normalized = toCountryCode(code);
                setPerson((prev) => ({
                  ...prev,
                  country: normalized,
                  phone: "",
                  dialCode: "",
                  nationalNumber: "",
                  phoneValid: false,
                }));
              }}
            />
            {isCountryLocked && (
              <small className="text-muted">
                Preset to {getCountryName(normalizedLock) || normalizedLock}
              </small>
            )}
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label">Phone</label>
            <div className={`form-control p-0 ${shouldShowPhoneErr ? "is-invalid" : ""}`}>
              <PhoneNumberInput
                key={phoneInputKey}
                countryName={selectedCountry}
                value={person.phone}
                onChange={(data) => {
                  setPerson(prev => ({
                    ...prev,
                    phone: "+" + data.phone,
                    dialCode: data.dialCode,
                    nationalNumber: data.nationalNumber,
                    phoneValid: data.isValid,
                  }));
                }}
                containerClass="react-tel-input w-100 ui-phone"
                inputClass="w-100 border-0 shadow-none"
                buttonClass="btn bg-body border-0"
                dropdownClass="dropdown-menu show"
              />
            </div>

            {shouldShowPhoneErr && <div className="text-danger small mt-1">Please enter a valid phone number.</div>}
          </div>
        </div>

        {actionContent}
      </form>
    </>
  );

  if (showPanel) {
    return (
      <section className="ui-panel">
        {content}
      </section>
    );
  }

  return content;
};

export default PersonForm;
